/* eslint-disable @typescript-eslint/no-explicit-any */
 
import { useEffect, useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import styles from "./CostCenters.module.css";
import { Add, ArrowBack, Search } from "@mui/icons-material";
import { cancelCostCenter, type ICostCenterFilter, loadCostCenters, toggleCostCenterStatus } from "../../../../services/financial";
import CostCenterList, { type ICostCenter } from "./CostCenterList";
import AddCostCenterModal from "./AddCostCenterModal/AddCostCenterModal";
import Button from "../../../../components/Button/Button";
import { useNavigate } from "react-router-dom";
import InputSearch from "../../../../components/InputSearch/InputSearch";

interface ILoadCostCenterResponse {
    rows: ICostCenter[];
    count: number;
    stats: {
        ativos: number;
        inativos: number;
        total: number;
    };
}

const CostCenter = () => {
    const navigate = useNavigate();

    const [isHidden, setIsHidden] = useState<{ addEntry?: boolean }>({
        addEntry: true,
    });

    const [filterValues, setFilterValues] = useState<ICostCenterFilter>({});
    const [nextPage, setNextPage] = useState(1);
    const [search, setSearch] = useState("");
    const [dataRender, setDataRender] = useState<ICostCenter[]>([]);
    const [entryToEdit, setEntryToEdit] = useState<ICostCenter | undefined>(undefined);

    const { data, refetch, isLoading } = useQuery<ILoadCostCenterResponse>({
        queryKey: ["costCenters", search, nextPage, filterValues],
        queryFn: () => loadCostCenters(search, nextPage, filterValues),
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    });


    useEffect(() => {
        setNextPage(1);
    }, [filterValues, search]);

    useEffect(() => {
        if (!data) return;

        const isFirstPage = nextPage === 1;
        const newValues = data.rows;

        if (data.count === 0) {
            setDataRender([]);
            return;
        }

        setDataRender((prev) => {
            if (isFirstPage) {
                return [...newValues];
            } else {
                const merged = [...prev, ...newValues];
                const unique = Array.from(
                    new Map(merged.map((item) => [item.uid_centro_custo, item])).values()
                );
                return unique;
            }
        });
    }, [data, nextPage]);

    const stats = useMemo(() => {
        if (data?.stats) {
            return data.stats;
        }
        return {
            ativos: 0,
            inativos: 0,
            total: data?.count || 0,
        };
    }, [data]);

    const handleCardClick = (type: 'ativos' | 'inativos' | 'total') => {
        setFilterValues((prev) => {
            if (type === 'ativos' && prev.ativo === true) return {};
            if (type === 'inativos' && prev.ativo === false) return {};
            if (type === 'total') return {};

            if (type === 'ativos') return { ativo: true };
            if (type === 'inativos') return { ativo: false };

            return {};
        });
    };

    const handleOpenModalAdd = () => {
        setEntryToEdit(undefined);
        setIsHidden((prev) => ({ ...prev, addEntry: false }));
    };

    const handleCloseModalAdd = () => {
        setEntryToEdit(undefined);
        setIsHidden((prev) => ({ ...prev, addEntry: true }));
    };

    const handleEditItem = (item: ICostCenter) => {
        setEntryToEdit(item);
        setIsHidden((prev) => ({ ...prev, addEntry: false }));
    };

    const handleToggleItemStatus = async (item: ICostCenter) => {
        try {
            await toggleCostCenterStatus(item.uid_centro_custo);
            refetch();
        } catch (error: any) {
            const msg = error?.message || "Erro ao alterar status";
            alert(msg);
            console.error(error);
        }
    };

    const handleDeleteItem = async (uid: string) => {
        const confirm = window.confirm("Deseja realmente excluir este centro de custo?");
        if (!confirm) return;

        try {
            await cancelCostCenter(uid);
            refetch();
        } catch (error: any) {
            const msg = error?.message || "Erro ao excluir centro de custo";
            alert(msg);
            console.error(error);
        }
    };

    const handleSuccess = (msg: string) => {
        console.log(msg);
    };

    return (
        <div className={styles.containerProjects}>
            <div className={styles.purchases}>
                <div className={styles.header}>
                    <h1>Centros de Custo</h1>
                    
                    <InputSearch
                        searchValue={search}
                        setSearchValue={setSearch}
                        placeholder="Pesquisar por nome"
                    />

                    <div className={styles.headerButtons}>
                        <p>
                            Qtd:<span>{data?.count ? data.count : 0}</span>
                        </p>

                        <Button
                            variant="contain"
                            onClick={handleOpenModalAdd}
                            title="Adicionar novo centro de custo">
                            <Add />
                        </Button>
                        <Button
                            variant="contain"
                            onClick={() => navigate(-1)}
                            title="Voltar para página anterior"
                        >
                            <ArrowBack />
                        </Button>
                    </div>
                </div>

                <div className={styles.financialStatsCard}>
                    <div className={styles.statLine}>
                        <div
                            className={`${styles.statItem} ${styles.ativosCard} ${filterValues.ativo === true ? styles.activeFilter : ''}`}
                            onClick={() => handleCardClick('ativos')}
                        >
                            <span className={styles.statText}>Ativos</span>
                            <span className={styles.statCount}>
                                {isLoading && nextPage === 1 ? "..." : stats.ativos}
                            </span>
                        </div>

                        <div
                            className={`${styles.statItem} ${styles.inativosCard} ${filterValues.ativo === false ? styles.activeFilter : ''}`}
                            onClick={() => handleCardClick('inativos')}
                        >
                            <span className={styles.statText}>Inativos</span>
                            <span className={styles.statCount}>
                                {isLoading && nextPage === 1 ? "..." : stats.inativos}
                            </span>
                        </div>

                        <div className={`${styles.statItem} ${styles.totalCard}`}>
                            <span className={styles.statText}>Total</span>
                            <span className={styles.statCount}>
                                {isLoading && nextPage === 1 ? "..." : stats.total}
                            </span>
                        </div>
                    </div>
                </div>

                <CostCenterList
                    rows={dataRender}
                    handleEdit={handleEditItem}
                    handleToggleStatus={handleToggleItemStatus}
                    handleDelete={handleDeleteItem}
                />

                <div className={styles.loadMore}>
                    {data?.count && dataRender.length < data.count ? (
                        <Button
                            variant="contain"
                            onClick={() => setNextPage((prev) => prev + 1)}>
                            {isLoading ? "Carregando..." : "Carregar Mais registros"}
                        </Button>
                    ) : data?.count === 0 ? null : (
                        <p className={styles.tableLastItem}>
                            Todos os registros foram carregados
                        </p>
                    )}
                </div>
            </div>

            {!isHidden.addEntry && (
                <AddCostCenterModal
                    onClose={handleCloseModalAdd}
                    entryToEdit={entryToEdit}
                    onSuccess={handleSuccess}
                    refetch={refetch}
                />
            )}
        </div>
    );
};

export default CostCenter;